/**
 * Date / date-time inputs that read `DD/MMM/YYYY` in every browser.
 *
 * A native `<input type="date">` (or `datetime-local`) always holds an
 * ISO value — `YYYY-MM-DD`, `YYYY-MM-DDTHH:mm` — but the text it DRAWS
 * follows the browser/OS locale, and nothing in HTML or CSS can change
 * that: en-US Chrome literally renders the placeholder "mm/dd/yyyy".
 * That is the one date format this app never uses (`utils/date-format.ts`
 * settled on `DD/MMM/YYYY` precisely because `03/04/2026` means two
 * different days on two sides of an ocean), so the calendar screen and
 * the form that feeds it disagreed with each other about what a date
 * looks like.
 *
 * So the native control is kept — it is the picker, the keyboard
 * handling, the validation, and the value — but made visually
 * transparent, and a span rendering the house format is drawn on top of
 * it. The input stays the real hit target (its
 * `::-webkit-calendar-picker-indicator` is stretched over the whole box),
 * so nothing about focus, tabbing or the picker changes.
 *
 * This pattern was first written inline on the Audit screen; it lives
 * here now because it had to be used in three places and a second copy
 * of a mask is how two screens start disagreeing again.
 */
import { IconCalendar } from '../icons';
import {
  DATE_PLACEHOLDER,
  DATE_TIME_PLACEHOLDER,
  formatIsoDate,
  formatIsoDateTime,
} from './date-field-format';
import './DateField.css';

interface MaskedFieldProps {
  /** Visible caption above the control. Omitted when the caller draws
   *  its own (the field is then just the masked input). */
  label?: string;
  /** The native ISO value — `YYYY-MM-DD` here, `YYYY-MM-DDTHH:mm` for
   *  `DateTimeField`. Empty string renders the placeholder. */
  value: string;
  onChange: (value: string) => void;
  /** The accessible name. Given as an `aria-label` on the input rather
   *  than relying on the wrapping `<label>`, because the visible caption
   *  is often a two-word abbreviation ("From") of what the control
   *  actually filters. */
  ariaLabel?: string;
  /** Native bounds, in the same ISO form as `value`. */
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Added to the outer `<label>` so a caller's own row layout still
   *  applies (`.nx-task-form__row`, `.nx-audit__date-filter`). */
  className?: string;
  'data-testid'?: string;
}

function MaskedField({
  type,
  placeholder,
  format,
  label,
  value,
  onChange,
  ariaLabel,
  min,
  max,
  disabled,
  className,
  'data-testid': testId,
}: MaskedFieldProps & {
  type: 'date' | 'datetime-local';
  placeholder: string;
  format: (value: string) => string;
}) {
  return (
    <label className={`nx-datefield${className ? ` ${className}` : ''}`}>
      {label !== undefined && <span>{label}</span>}
      <span className="nx-datefield__wrap">
        <input
          type={type}
          className="nx-datefield__input"
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label={ariaLabel}
          data-testid={testId}
        />
        {/* `aria-hidden`: it is a second rendering of the input's own
            value, and a screen reader announcing both would read the
            date twice. */}
        <span className="nx-datefield__display" aria-hidden="true">
          {value ? format(value) : placeholder}
        </span>
        <IconCalendar size={14} className="nx-datefield__icon" />
      </span>
    </label>
  );
}

/** A calendar date, displayed `DD/MMM/YYYY`. Value is `YYYY-MM-DD`. */
export function DateField(props: MaskedFieldProps) {
  return (
    <MaskedField
      {...props}
      type="date"
      placeholder={DATE_PLACEHOLDER}
      format={formatIsoDate}
    />
  );
}

/** A date and wall-clock time, displayed `DD/MMM/YYYY, HH:mm`. Value is
 *  `YYYY-MM-DDTHH:mm` — exactly what `datetime-local` holds, so this is
 *  a drop-in for one. */
export function DateTimeField(props: MaskedFieldProps) {
  return (
    <MaskedField
      {...props}
      type="datetime-local"
      placeholder={DATE_TIME_PLACEHOLDER}
      format={formatIsoDateTime}
    />
  );
}
